/**
 * A curva de uma dissipação (spec 07 §5.5 e §6.1, item 5; onda 313, R11): u × √t, log t ou t, com a
 * reta do trecho inicial, uᵢ, u₀ (na cor da água), u₅₀ e o t₅₀ marcados. O t₅₀ é sempre «pela reta» (a
 * regra `dis.t50-pela-reta` do motor), e «extrapolado — a curva não chegou a u₅₀» só quando a curva
 * medida não chega a u₅₀.
 *
 * Com `editavel` e na vista √t, duas alças sobre a reta (a 10 % e a 60 % da janela visível — a reta do
 * `.cpt`, com as pontas em x = ±10.000, ganha alças dentro da janela sem mudar): arrastar uma alça cola
 * na leitura mais próxima (o ímã), o Shift a solta — o gesto do Taylor do adensamento. Cada passo do
 * arrasto devolve a reta nova (`aoMudarReta`), e o resultado se recalcula ao vivo. As alças chegam também
 * pelo teclado (rodada do agent-browser da 313): o Tab para em cada uma, as setas a levam de leitura em
 * leitura pela curva (→ e ↓ adiante, ← e ↑ atrás — o ímã) e, com o Shift, ela anda solta, 4 px por toque.
 *
 * Onda 315 (Tarefa 315.5b): `leituraEscolhida` acende uma leitura na curva (a linha escolhida na tabela tempo × u
 * da tela), e `aoEscolherLeitura` devolve a leitura mais perto de um clique no desenho (a tabela acende a linha).
 */
import type { JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import type { InterpretacaoDaDissipacao, Reta, Serie } from '../motor';
import { FRASE_T50_EXTRAPOLADO, alcasVigentes, imantar, retaPelasAlcas, xDoTempo } from './alcas';
import type { AlcasLocais, EixoDoTempo, PontoDoPlano } from './alcas';
import { dominioDe, escala, marcas, numeroBr } from './escalas';
import { useLargura } from './useLargura';

const M = { esq: 56, dir: 16, topo: 12, baixo: 44 };
/** O raio do ímã, em pixel. */
const RAIO_DO_IMA = 12;
/** O passo da alça solta pelo teclado (Shift + seta), em pixel. */
const PASSO_SOLTO = 4;

/**
 * A leitura vizinha da alça pela curva, em pixel: a primeira com x adiante (ou a última atrás) do x dela; sem vizinha
 * naquele sentido, null. Os pontos sem número ficam de fora.
 */
function leituraVizinha(curva: { x: ArrayLike<number>; y: ArrayLike<number> }, x: number, adiante: boolean): PontoDoPlano | null {
  let melhor: PontoDoPlano | null = null;
  const n = Math.min(curva.x.length, curva.y.length);
  for (let i = 0; i < n; i++) {
    const cx = curva.x[i];
    const cy = curva.y[i];
    if (!Number.isFinite(cx) || !Number.isFinite(cy)) continue;
    if (adiante ? cx > x + 1e-6 && (melhor === null || cx < melhor.x) : cx < x - 1e-6 && (melhor === null || cx > melhor.x)) {
      melhor = { x: cx, y: cy };
    }
  }
  return melhor;
}

/** O u da reta em √t = x. */
const naReta = (reta: Reta, x: number): number => reta.y1 + ((x - reta.x1) * (reta.y2 - reta.y1)) / (reta.x2 - reta.x1);

/** O rótulo do eixo de cada vista. */
const ROTULO_DO_EIXO: Readonly<Record<EixoDoTempo, string>> = {
  raiz: '√t (√s)', log: 't (s), escala log', linear: 't (s)',
};

/** A curva de uma dissipação, com a reta, os marcos e, editável na vista √t, as alças. */
export function CurvaDeDissipacao(p: {
  tempo: Serie;
  u: Serie;
  eixo: EixoDoTempo;
  interpretacao: InterpretacaoDaDissipacao;
  editavel: boolean;
  aoMudarReta?(r: Reta): void;
  /** A leitura acesa (o índice na curva); null ou sem ela, nenhuma. */
  leituraEscolhida?: number | null;
  /** Um clique no desenho escolhe a leitura mais perto (o índice na curva). */
  aoEscolherLeitura?(i: number): void;
}): JSX.Element {
  const { tempo, u, eixo, interpretacao: s } = p;
  const [ref, largura] = useLargura<HTMLElement>();
  const larguraPlot = Math.max(220, largura - M.esq - M.dir);
  const alturaPlot = Math.round(Math.max(220, Math.min(360, larguraPlot * 0.55)));

  // O x de cada leitura na vista, e as janelas dos dois eixos.
  const xs = useMemo(() => Float64Array.from(tempo, (t) => xDoTempo(t, eixo)), [tempo, eixo]);
  const dominioX = useMemo((): [number, number] => {
    let tMax = 0;
    let tMin = Infinity;
    for (let i = 0; i < tempo.length; i++) {
      if (tempo[i] > tMax) tMax = tempo[i];
      if (tempo[i] > 0 && tempo[i] < tMin) tMin = tempo[i];
    }
    if (eixo === 'raiz') return [0, Math.max(1, Math.sqrt(tMax))];
    if (eixo === 'log') {
      const de = Number.isFinite(tMin) ? Math.floor(Math.log10(tMin)) : 0;
      return [de, Math.max(de + 1, Math.ceil(Math.log10(Math.max(tMax, 1))))];
    }
    return [0, Math.max(1, tMax)];
  }, [tempo, eixo]);
  const dominioY = useMemo((): [number, number] => {
    const [a, b] = dominioDe([u, [s.ui, s.u0, s.u50]], 'linear');
    const folga = (b - a) * 0.06;
    return [a - folga, b + folga];
  }, [u, s.ui, s.u0, s.u50]);
  const ex = escala('linear', dominioX, [M.esq, M.esq + larguraPlot]);
  const ey = escala('linear', dominioY, [M.topo + alturaPlot, M.topo]);

  // A curva e a reta (na vista √t uma reta; nas outras, u = uᵢ + m·√t amostrada).
  const curva = useMemo(() => {
    const partes: string[] = [];
    let novo = true;
    for (let i = 0; i < xs.length; i++) {
      const x = ex.para(xs[i]);
      const y = ey.para(u[i]);
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        novo = true;
        continue;
      }
      partes.push(`${novo ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`);
      novo = false;
    }
    return partes.join('');
  }, [xs, u, ex, ey]);
  // As alças: quem decide é `alcasVigentes` (as locais durante o arrasto e até o pai responder; a resposta
  // dele, se passa pelas duas, vira a reta que as segura; outra reta do pai as derruba). O que ela muda em
  // `local` (a resposta do pai guardada, ou as locais caindo) vai ao estado depois do desenho.
  const [local, setLocal] = useState<AlcasLocais | null>(null);
  const [emArrasto, setEmArrasto] = useState(false);
  const arrastando = useRef<number | null>(null);
  const dosPais = s.reta;
  const vigentes = alcasVigentes({ local, retaDoPai: dosPais, arrastando: emArrasto, janela: dominioX });
  useEffect(() => {
    if (vigentes.local !== local) setLocal(vigentes.local);
  }, [vigentes.local, local]);
  const reta: Reta = vigentes.reta;
  const comAlcas = p.editavel && eixo === 'raiz' && !!p.aoMudarReta;
  const atuais: [PontoDoPlano, PontoDoPlano] = vigentes.alcas;
  const caminhoDaReta = useMemo(() => {
    const partes: string[] = [];
    const n = 120;
    for (let k = 0; k <= n; k++) {
      const x = dominioX[0] + ((dominioX[1] - dominioX[0]) * k) / n;
      const t = eixo === 'raiz' ? x * x : eixo === 'log' ? 10 ** x : x;
      const y = ey.para(naReta(reta, Math.sqrt(t)));
      if (Number.isFinite(y)) partes.push(`${partes.length === 0 ? 'M' : 'L'}${ex.para(x).toFixed(1)} ${y.toFixed(1)}`);
    }
    return partes.join('');
  }, [reta, dominioX, eixo, ex, ey]);
  const curvaEmPixel = useMemo(() => ({ x: Float64Array.from(xs, (x) => ex.para(x)), y: Float64Array.from(u, (v) => ey.para(v)) }), [xs, u, ex, ey]);
  const noSvg = (e: MouseEvent<SVGElement>): PontoDoPlano => {
    const alvo = e.currentTarget as SVGElement;
    const caixa = (alvo.ownerSVGElement ?? alvo).getBoundingClientRect();
    return { x: e.clientX - caixa.left, y: e.clientY - caixa.top };
  };
  /** A alça k num ponto do desenho (pixel): as duas alças, a reta por elas, e a reta ao pai. */
  const moverAlca = (k: number, alvo: PontoDoPlano): void => {
    if (!p.aoMudarReta) return;
    const novo: PontoDoPlano = { x: ex.de(alvo.x), y: ey.de(alvo.y) };
    const outro = atuais[1 - k];
    if (!Number.isFinite(novo.x) || !Number.isFinite(novo.y) || Math.abs(novo.x - outro.x) < 1e-9) return;
    const par: [PontoDoPlano, PontoDoPlano] = k === 0 ? [novo, outro] : [outro, novo];
    setLocal({ alcas: par, retaDoPai: dosPais });
    p.aoMudarReta(retaPelasAlcas(par[0], par[1]));
  };
  /** As setas na alça: de leitura em leitura pela curva (o ímã); com o Shift, solta, um passo de 4 px. */
  const teclaNaAlca = (k: number, e: KeyboardEvent<SVGCircleElement>): void => {
    const seta = e.key;
    if (seta !== 'ArrowLeft' && seta !== 'ArrowRight' && seta !== 'ArrowUp' && seta !== 'ArrowDown') return;
    e.preventDefault();
    const q = atuais[k];
    const aqui = { x: ex.para(q.x), y: ey.para(q.y) };
    if (e.shiftKey) {
      const dx = seta === 'ArrowRight' ? PASSO_SOLTO : seta === 'ArrowLeft' ? -PASSO_SOLTO : 0;
      const dy = seta === 'ArrowDown' ? PASSO_SOLTO : seta === 'ArrowUp' ? -PASSO_SOLTO : 0;
      moverAlca(k, { x: aqui.x + dx, y: aqui.y + dy });
      return;
    }
    const vizinha = leituraVizinha(curvaEmPixel, aqui.x, seta === 'ArrowRight' || seta === 'ArrowDown');
    if (vizinha) moverAlca(k, vizinha);
  };

  const marcasX = eixo === 'log'
    ? Array.from({ length: dominioX[1] - dominioX[0] + 1 }, (_, k) => dominioX[0] + k)
    : marcas('linear', dominioX, 6);
  const marcasY = marcas('linear', dominioY, 5);
  const passoY = marcasY.length > 1 ? marcasY[1] - marcasY[0] : 1;
  const casasY = Math.max(0, Math.ceil(-Math.log10(passoY) - 1e-9));
  const rotuloX = (v: number): string => {
    if (eixo === 'log') return numeroBr(10 ** v, v < 0 ? -v : 0);
    const passo = marcasX.length > 1 ? marcasX[1] - marcasX[0] : 1;
    return numeroBr(v, Math.max(0, Math.ceil(-Math.log10(passo) - 1e-9)));
  };
  /** A leitura mais perto de um ponto do desenho (pixel), entre as que têm número. */
  const leituraMaisPerto = (q: PontoDoPlano): number => {
    let melhor = -1;
    let dist = Infinity;
    for (let i = 0; i < curvaEmPixel.x.length; i++) {
      const dx = curvaEmPixel.x[i] - q.x;
      const dy = curvaEmPixel.y[i] - q.y;
      const d2 = dx * dx + dy * dy;
      if (Number.isFinite(d2) && d2 < dist) { dist = d2; melhor = i; }
    }
    return melhor;
  };
  const escolhida = p.leituraEscolhida ?? null;
  const pontoEscolhido = escolhida !== null && escolhida >= 0 && escolhida < xs.length
    && Number.isFinite(curvaEmPixel.x[escolhida]) && Number.isFinite(curvaEmPixel.y[escolhida])
    ? { x: curvaEmPixel.x[escolhida], y: curvaEmPixel.y[escolhida] } : null;
  const xT50 = xDoTempo(s.t50, eixo);
  const textoDoT50 = Number.isFinite(s.t50)
    ? `t₅₀ = ${numeroBr(s.t50, 2)} s, pela reta${s.t50Extrapolado ? ` · ${FRASE_T50_EXTRAPOLADO}` : ''}`
    : 't₅₀ sem valor: a reta não chega a u₅₀';

  return (
    <figure ref={ref} className="cptu-dissipacao">
      {largura > 0 ? (
        // Com as alças (controles do teclado), o desenho é um grupo: dentro de uma imagem elas não teriam nome.
        <svg width={M.esq + larguraPlot + M.dir} height={M.topo + alturaPlot + M.baixo} role={comAlcas ? 'group' : 'img'}
          aria-label={`Dissipação: u contra ${ROTULO_DO_EIXO[eixo]}. ${textoDoT50}`}>
          <rect className="cptu-moldura" x={M.esq} y={M.topo} width={larguraPlot} height={alturaPlot} />
          {marcasX.map((v) => (
            <g key={`x${v}`}>
              <line className="cptu-grade" x1={ex.para(v)} x2={ex.para(v)} y1={M.topo} y2={M.topo + alturaPlot} />
              <text className="cptu-texto cptu-texto-2" x={ex.para(v)} y={M.topo + alturaPlot + 16} textAnchor="middle">{rotuloX(v)}</text>
            </g>
          ))}
          {marcasY.map((v) => (
            <g key={`y${v}`}>
              <line className="cptu-grade" x1={M.esq} x2={M.esq + larguraPlot} y1={ey.para(v)} y2={ey.para(v)} />
              <text className="cptu-texto cptu-texto-2" x={M.esq - 6} y={ey.para(v) + 4} textAnchor="end">{numeroBr(v, casasY)}</text>
            </g>
          ))}
          <text className="cptu-texto" x={M.esq + larguraPlot / 2} y={M.topo + alturaPlot + 36} textAnchor="middle">
            {ROTULO_DO_EIXO[eixo]}
          </text>
          <text className="cptu-texto" transform={`translate(16 ${M.topo + alturaPlot / 2}) rotate(-90)`} textAnchor="middle">u (kPa)</text>

          <svg x={M.esq} y={M.topo} width={larguraPlot} height={alturaPlot} overflow="hidden">
            <g transform={`translate(${-M.esq} ${-M.topo})`}>
              <line className="cptu-u0" x1={M.esq} x2={M.esq + larguraPlot} y1={ey.para(s.u0)} y2={ey.para(s.u0)} />
              <line className="cptu-u50" x1={M.esq} x2={M.esq + larguraPlot} y1={ey.para(s.u50)} y2={ey.para(s.u50)} />
              {Number.isFinite(xT50) ? (
                <line className="cptu-t50" x1={ex.para(xT50)} x2={ex.para(xT50)} y1={M.topo} y2={M.topo + alturaPlot} />
              ) : null}
              <path className="cptu-serie serie-u2" d={curva} />
              <path className="cptu-reta" d={caminhoDaReta} />
              {eixo === 'raiz' && Number.isFinite(s.ui) ? <circle className="cptu-marco" cx={ex.para(0)} cy={ey.para(s.ui)} r={4} /> : null}
              {pontoEscolhido ? (
                <circle className="cptu-leitura-escolhida" cx={pontoEscolhido.x} cy={pontoEscolhido.y} r={6} data-leitura={escolhida ?? undefined} />
              ) : null}
            </g>
          </svg>
          {p.aoEscolherLeitura ? (
            <rect className="cptu-captura-da-curva" x={M.esq} y={M.topo} width={larguraPlot} height={alturaPlot}
              onClick={(e) => {
                const i = leituraMaisPerto(noSvg(e));
                if (i >= 0) p.aoEscolherLeitura?.(i);
              }} />
          ) : null}
          <text className="cptu-texto cptu-texto-2" x={M.esq + larguraPlot - 4} y={ey.para(s.u0) - 4} textAnchor="end">
            u₀ {numeroBr(s.u0, 2)} kPa
          </text>
          <text className="cptu-texto cptu-texto-2" x={M.esq + larguraPlot - 4} y={ey.para(s.u50) - 4} textAnchor="end">
            u₅₀ {numeroBr(s.u50, 2)} kPa
          </text>
          {eixo === 'raiz' && Number.isFinite(s.ui) ? (
            <text className="cptu-texto" x={M.esq + 8} y={ey.para(s.ui) - 6}>uᵢ {numeroBr(s.ui, 2)} kPa</text>
          ) : null}
          {Number.isFinite(xT50) ? (
            <text className="cptu-texto cptu-t50-texto" x={ex.para(xT50) + 4} y={M.topo + 14}>t₅₀</text>
          ) : null}

          {comAlcas ? atuais.map((q, k) => (
            <circle
              key={k}
              className="cptu-alca"
              cx={ex.para(q.x)}
              cy={ey.para(q.y)}
              r={7}
              tabIndex={0}
              role="slider"
              aria-label={`Alça ${k + 1} da reta: as setas a levam de leitura em leitura pela curva; com Shift, ela anda solta`}
              aria-orientation="horizontal"
              aria-valuemin={dominioX[0]}
              aria-valuemax={dominioX[1]}
              aria-valuenow={q.x}
              aria-valuetext={`√t ${numeroBr(q.x, 2)} √s, u ${numeroBr(q.y, 2)} kPa`}
              onKeyDown={(e) => teclaNaAlca(k, e)}
              onPointerDown={(e) => {
                if (e.button !== 0) return;
                e.currentTarget.setPointerCapture?.(e.pointerId);
                arrastando.current = k;
                setEmArrasto(true);
              }}
              onPointerMove={(e) => {
                if (arrastando.current !== k) return;
                moverAlca(k, imantar(noSvg(e), curvaEmPixel, RAIO_DO_IMA, e.shiftKey));
              }}
              onPointerUp={() => { arrastando.current = null; setEmArrasto(false); }}
              onPointerCancel={() => { arrastando.current = null; setEmArrasto(false); }}
            />
          )) : null}
        </svg>
      ) : null}
      <figcaption className="cptu-dissipacao-legenda">
        <p className={s.t50Extrapolado ? 'cptu-aviso' : 'cptu-nota'}>{textoDoT50}</p>
        {comAlcas ? (
          <p className="cptu-nota">
            Arraste as alças para ajustar o trecho reto (Shift solta do ponto). Pelo teclado: Tab chega à alça, as setas a
            levam de leitura em leitura, e com Shift ela anda solta.
          </p>
        ) : null}
        {p.editavel && eixo !== 'raiz' ? <p className="cptu-nota">As alças da reta ficam na vista √t.</p> : null}
      </figcaption>
    </figure>
  );
}
